import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Box, Stack, Typography, TextField } from '@mui/material';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

import { PaymentGatewayType, paymentGatewayTypes } from '../../../store/modules/merchant/types';
import CardAcceptance from '../../PaymentsAndProcessing/components/CardAcceptance';
import {
  CARD_ACCEPTANCE_MIN_ONE_HINT,
  hasAtLeastOneAcceptedCardNetwork,
} from '../../../util/cardAcceptance';
import { CardBrand, CardBrandSchema } from '../../../util/constants';
import SaveBar from '../../../common/SaveBar/SaveBar';
import Switch from '../../../common/Switch';
import {
  FormSection,
  NameDomainFields,
  PaymentGatewayFields,
} from '../../../common/MerchantCreate';

type FormValues = {
  name: string;
  domain: string;
  paymentGateway: PaymentGatewayType | 'none';
  paymentGatewayData?: Record<string, string>;
  nonAcceptedCardBrands: CardBrand[];
  inheritNonAcceptedCardBrands: boolean;
};

const getValidationSchema = ({
  isPaymentGatewayDataRequired,
}: {
  isPaymentGatewayDataRequired: boolean;
}) =>
  z
    .object({
      name: z.string().min(1),
      domain: z.string().url().optional().or(z.literal('')),
      paymentGateway: z.enum([...paymentGatewayTypes, 'none']),
      paymentGatewayData: z.record(z.string(), z.string().min(1)).optional(),
      nonAcceptedCardBrands: z.array(CardBrandSchema),
      inheritNonAcceptedCardBrands: z.boolean().optional(),
    })
    .refine(
      (data) => {
        const noPaymentGateway = data.paymentGateway === 'none';
        if (noPaymentGateway) {
          return !isPaymentGatewayDataRequired;
        }
        return true;
      },
      {
        message: 'Payment gateway data is required',
        path: ['paymentGateway'],
      },
    )
    .refine(
      (data) =>
        data.inheritNonAcceptedCardBrands ||
        hasAtLeastOneAcceptedCardNetwork(data.nonAcceptedCardBrands),
      {
        message: CARD_ACCEPTANCE_MIN_ONE_HINT,
        path: ['nonAcceptedCardBrands'],
      },
    );

type Props = {
  paymentGateway: PaymentGatewayType;
  parentMerchantName: string;
  onSubmit: (data: FormValues) => void;
  isSubmitting: boolean;
  nonAcceptedCardBrands?: CardBrand[];
  isPaymentGatewayDataRequired?: boolean;
  readOnly?: boolean;
};

export const CreateChildMerchantForm = ({
  paymentGateway,
  parentMerchantName,
  onSubmit,
  isSubmitting,
  nonAcceptedCardBrands = [],
  isPaymentGatewayDataRequired = true,
  readOnly = false,
}: Props) => {
  const methods = useForm<FormValues>({
    resolver: zodResolver(getValidationSchema({ isPaymentGatewayDataRequired })),
    defaultValues: {
      name: '',
      domain: '',
      paymentGateway: isPaymentGatewayDataRequired ? paymentGateway : 'none',
      paymentGatewayData: {},
      nonAcceptedCardBrands,
      inheritNonAcceptedCardBrands: true,
    },
  });

  const {
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, dirtyFields },
  } = methods;

  const inheritNonAcceptedCardBrands = watch('inheritNonAcceptedCardBrands');

  const submitAndReset = async (data: FormValues) => {
    try {
      await onSubmit(data);
      reset(undefined, { keepDirty: false, keepDirtyValues: false });
    } catch {
      // Do nothing - the onSubmit funciton will handle the error and display a toast message
    }
  };

  return (
    <FormProvider {...methods}>
      <Box
        component="form"
        id="create-child-merchant-form"
        onSubmit={handleSubmit(submitAndReset)}
        sx={{ pb: 6 }}
      >
        <Stack spacing={4}>
          <FormSection title="Account Details">
            <TextField
              label="Parent Merchant"
              value={parentMerchantName}
              disabled
              InputLabelProps={{ shrink: true }}
            />
            <NameDomainFields nameLabel="Child Account Name" readOnly={readOnly} />
          </FormSection>
          <PaymentGatewayFields readOnly={readOnly} fieldsRequired={isPaymentGatewayDataRequired} />
          <FormSection title="Payment Settings">
            <CardAcceptance
              key={inheritNonAcceptedCardBrands ? 'inherit' : 'not-inherit'}
              nonAcceptedCardBrands={watch('nonAcceptedCardBrands')}
              onNonAcceptedCardBrandsChange={(value) => {
                setValue('nonAcceptedCardBrands', value, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
              }}
              disabled={inheritNonAcceptedCardBrands || readOnly}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Switch
                isChecked={inheritNonAcceptedCardBrands}
                onToggle={(value) => {
                  setValue('inheritNonAcceptedCardBrands', value, { shouldDirty: true });
                  if (value) {
                    setValue('nonAcceptedCardBrands', nonAcceptedCardBrands);
                  }
                }}
                isDisabled={readOnly}
                aria-label="Inherit accepted card brands from parent merchant Switch"
              />
              <Typography
                sx={{
                  fontSize: '14px',
                  color: 'common.black',
                }}
              >
                Inherit accepted card brands from parent merchant
              </Typography>
            </Box>
          </FormSection>
        </Stack>
      </Box>
      <SaveBar
        text={isSubmitting ? 'Creating child merchant...' : 'Create child merchant?'}
        show={Object.keys(dirtyFields).length > 0 && !readOnly}
        disableSave={Object.keys(errors).length > 0 || readOnly}
        onCancel={() => {
          reset(undefined, { keepDirty: false, keepDirtyValues: false });
        }}
        formId="create-child-merchant-form"
        saveLoading={isSubmitting}
      />
    </FormProvider>
  );
};
